import React, { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { Button, CircularProgress, Paper, Typography } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CloseIcon from '@material-ui/icons/Close';
import { PageBarHeader } from '../../components/migrated/pageheader';
import { useEnvironment, useHelmet, useSnackbar } from '../../hooks';
import { useSubmitInvoiceXml } from './service';
import { useStyles } from './styles';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export const SendInvoice = () => {
  const { TITLE } = useEnvironment();
  useHelmet({ title: `${TITLE} - Enviar Nota Fiscal` });
  const classes = useStyles();
  const { Snackbar, toggleSnackbar } = useSnackbar();
  const { submitInvoiceXml } = useSubmitInvoiceXml();
  const inputFileRef = useRef<HTMLInputElement>({} as HTMLInputElement);

  const [file, setFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSelectFile = () => {
    inputFileRef.current.click();
  };

  const handleFile = (candidate: File | undefined) => {
    if (!candidate) return;

    if (!candidate.name.toLowerCase().endsWith('.xml')) {
      toggleSnackbar({ message: 'Apenas arquivos XML são aceitos.', type: 'warning' });
      return;
    }

    if (candidate.size > MAX_FILE_SIZE_BYTES) {
      toggleSnackbar({ message: 'O arquivo excede o tamanho máximo de 10MB.', type: 'warning' });
      return;
    }

    setFile(candidate);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleRemoveFile = () => setFile(null);

  const handleSubmit = async () => {
    if (!file) {
      toggleSnackbar({ message: 'Selecione o arquivo XML da nota fiscal.', type: 'warning' });
      return;
    }

    setSubmitting(true);
    const result = await submitInvoiceXml(file);
    setSubmitting(false);

    if (result.success) {
      toggleSnackbar({ message: 'Nota fiscal enviada com sucesso!', type: 'success' });
      setFile(null);
    } else {
      toggleSnackbar({
        message: result.message || 'Ocorreu um problema ao enviar a nota fiscal. Por favor, informe ao suporte.',
        type: 'error'
      });
    }
  };

  return (
    <div className={classes.page}>
      <Snackbar />
      <div className={classes.headerWrapper}>
        <PageBarHeader title="Enviar Nota Fiscal" />
      </div>

      <div className={classes.root}>
        <Paper className={classes.card} variant="outlined">
          <div className={classes.cardBody}>
            <input ref={inputFileRef} type="file" accept=".xml" hidden onChange={handleInputChange} />

            {!file && (
              <>
                <div
                  className={`${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ''}`}
                  onClick={handleSelectFile}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <CloudUploadIcon className={classes.uploadIcon} />
                  <Typography variant="h6" className={classes.dropzoneText}>
                    Arraste seu arquivo ou clique abaixo para selecioná-lo
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectFile();
                    }}
                  >
                    Enviar Arquivo
                  </Button>
                </div>
                <Typography variant="caption" className={classes.helperText}>
                  Formatos suportados: XML (Max 10MB) — apenas 1 arquivo por envio
                </Typography>
              </>
            )}

            {file && (
              <>
                <div className={classes.filePreview}>
                  <InsertDriveFileIcon color="primary" />
                  <Typography variant="body2" className={classes.fileNameText}>
                    {file.name}
                  </Typography>
                  <CloseIcon fontSize="small" style={{ cursor: 'pointer' }} onClick={handleRemoveFile} />
                </div>

                <div className={classes.actions}>
                  <Button onClick={handleRemoveFile} disabled={submitting}>
                    Cancelar
                  </Button>
                  <Button variant="contained" color="primary" onClick={handleSubmit} disabled={submitting}>
                    Enviar Nota
                  </Button>
                </div>
              </>
            )}

            {submitting && (
              <div className={classes.loader}>
                <CircularProgress />
              </div>
            )}
          </div>
        </Paper>
      </div>
    </div>
  );
};
